import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { fmt } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const MAX_YEARS = 50;

// Simulates year-by-year: withdraw first (inflation-adjusted after year 1),
// then apply growth to what's left - the standard, conservative ordering
// used by most retirement calculators (withdrawing at the start of the
// year rather than the end).
function simulate(startingBalance, withdrawalRatePct, growthRatePct, inflationPct) {
  const points = [{ year: 0, balance: startingBalance }];
  let balance = startingBalance;
  let annualWithdrawal = startingBalance * (withdrawalRatePct / 100);
  let depletedAtYear = null;

  for (let year = 1; year <= MAX_YEARS; year++) {
    balance -= annualWithdrawal;
    if (balance <= 0) {
      balance = 0;
      if (depletedAtYear === null) depletedAtYear = year;
    } else {
      balance *= 1 + growthRatePct / 100;
    }
    points.push({ year, balance });
    annualWithdrawal *= 1 + inflationPct / 100;
    if (balance === 0 && depletedAtYear !== null && year > depletedAtYear) break;
  }

  return { points, depletedAtYear };
}

export default function WithdrawalCalculator({ startingBalance }) {
  const [collapsed, toggleCollapsed] = useCollapsible('withdrawalCalculator');
  const [balance, setBalance] = useState(Math.max(Math.round(startingBalance || 0), 0));
  const [withdrawalRate, setWithdrawalRate] = useState(4);
  const [growthRate, setGrowthRate] = useState(6);
  const [inflation, setInflation] = useState(2.5);

  const { points, depletedAtYear } = useMemo(
    () => simulate(balance, withdrawalRate, growthRate, inflation),
    [balance, withdrawalRate, growthRate, inflation]
  );

  const lastsIndefinitely = depletedAtYear === null;
  const annualWithdrawal = balance * (withdrawalRate / 100);
  const currentYear = new Date().getFullYear();
  const maxBalance = Math.max(...points.map((p) => p.balance));
  const yAxisFormatter = (v) => (maxBalance >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`);
  const xTickInterval = Math.max(Math.ceil(points.length / 12) - 1, 0);

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>🧮 How Long Will It Last?</h2>
        </button>
      </div>

      {!collapsed && (
        <>
          <p style={styles.subtext}>
            Drag the sliders to see how different withdrawal rates and market conditions play out over time. This is a simple
            projection (fixed annual growth, no market ups and downs) - real markets are far less smooth than this line.
          </p>

          <div style={styles.sliderGroup}>
            <label style={styles.sliderLabel}>
              <span>Starting balance</span>
              <span style={styles.sliderValue}>{fmt.currency(balance)}</span>
            </label>
            <input
              type="range"
              min="0"
              max={Math.max(balance * 2, 500000)}
              step="5000"
              value={balance}
              onChange={(e) => setBalance(Number(e.target.value))}
              style={styles.slider}
            />
          </div>

          <div style={styles.sliderGroup}>
            <label style={styles.sliderLabel}>
              <span>Annual withdrawal rate</span>
              <span style={styles.sliderValue}>{withdrawalRate.toFixed(2)}% &middot; {fmt.currency(annualWithdrawal)}/yr</span>
            </label>
            <input
              type="range"
              min="0"
              max="12"
              step="0.25"
              value={withdrawalRate}
              onChange={(e) => setWithdrawalRate(Number(e.target.value))}
              style={styles.slider}
            />
          </div>

          <div style={styles.sliderGroup}>
            <label style={styles.sliderLabel}>
              <span>Expected annual market growth</span>
              <span style={styles.sliderValue}>{growthRate.toFixed(2)}%</span>
            </label>
            <input
              type="range"
              min="-5"
              max="12"
              step="0.25"
              value={growthRate}
              onChange={(e) => setGrowthRate(Number(e.target.value))}
              style={styles.slider}
            />
          </div>

          <div style={styles.sliderGroup}>
            <label style={styles.sliderLabel}>
              <span>Inflation (grows your withdrawal $ each year)</span>
              <span style={styles.sliderValue}>{inflation.toFixed(2)}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="6"
              step="0.25"
              value={inflation}
              onChange={(e) => setInflation(Number(e.target.value))}
              style={styles.slider}
            />
          </div>

          <div style={{
            ...styles.resultBanner,
            background: lastsIndefinitely ? 'rgba(0,212,170,0.08)' : 'rgba(239,68,68,0.08)',
            border: `1px solid ${lastsIndefinitely ? 'rgba(0,212,170,0.3)' : 'rgba(239,68,68,0.3)'}`,
          }}>
            {lastsIndefinitely
              ? `At these rates, the balance holds (or grows) indefinitely over a ${MAX_YEARS}-year horizon.`
              : `At these rates, the balance runs out in about ${depletedAtYear} year${depletedAtYear === 1 ? '' : 's'}.`}
          </div>

          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={points} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <XAxis
                dataKey="year"
                tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }}
                axisLine={false}
                tickLine={false}
                interval={xTickInterval}
                tickFormatter={(y) => currentYear + y}
                label={{ value: 'Year', position: 'bottom', offset: 0, fill: '#6b7280', fontSize: 11, fontFamily: "'Syne', sans-serif" }}
              />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} tickFormatter={yAxisFormatter} />
              <Tooltip
                contentStyle={{ background: '#1a2332', border: '1px solid #1e2a3a', borderRadius: '8px' }}
                formatter={(v) => [fmt.currency(v), 'Balance']}
                labelFormatter={(y) => `Year ${currentYear + y}`}
              />
              <ReferenceLine y={0} stroke="#374151" />
              <Line type="monotone" dataKey="balance" stroke={lastsIndefinitely ? '#00d4aa' : '#ef4444'} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' },
  title: { margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#fff', fontFamily: "'Syne', sans-serif" },
  collapseBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 },
  chevron: { color: '#6b7280', fontSize: '0.8rem', width: '0.8rem' },
  subtext: { color: '#6b7280', fontSize: '0.76rem', lineHeight: 1.5, margin: '0 0 1.2rem 0' },
  sliderGroup: { marginBottom: '1.1rem' },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#9ca3af',
    fontSize: '0.78rem',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.4rem',
  },
  sliderValue: { color: '#fff', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  slider: { width: '100%', accentColor: '#00d4aa' },
  resultBanner: {
    borderRadius: '10px',
    padding: '0.8rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#e5e7eb',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '1rem',
  },
};
